import React, { useCallback, useEffect, useMemo, useState } from "react";
import { App as CapApp } from "@capacitor/app";

import { reisorProMarketRequest } from "../utils/reisorProMarketHttp.js";
import {
  clearPhoneVerificationReturnSignal,
  hasFreshPhoneVerificationReturnSignal,
} from "../utils/phoneVerificationReturnSignal.js";
import {
  readDriverPhoneVerificationState,
  saveDriverPhoneVerificationState,
} from "../utils/driverPhoneVerificationState.js";

const ROLE = "DRIVER";
const REGISTRATION_COUNTRY = "OTHER";

function digits(value) {
  return String(value || "").replace(/\D/g, "");
}

function telegramNativeMiniAppUrl(value) {
  try {
    const url = new URL(String(value || "").trim());
    const host = String(url.hostname || "").toLowerCase();
    if (!["t.me", "telegram.me", "www.t.me"].includes(host)) return "";

    const parts = url.pathname.split("/").map((item) => item.trim()).filter(Boolean);
    const botUsername = String(parts[0] || "").replace(/^@+/, "");
    if (!/^[A-Za-z0-9_]{5,32}$/.test(botUsername)) return "";

    const params = new URLSearchParams();
    params.set("domain", botUsername);

    const shortName = String(parts[1] || "");
    if (shortName) params.set("appname", shortName);

    for (const key of ["startapp", "mode"]) {
      const raw = url.searchParams.get(key);
      if (raw) params.set(key, raw);
    }

    return `tg://resolve?${params.toString()}`;
  } catch {
    return "";
  }
}

function openTelegramUrl(telegramUrl) {
  const httpsUrl = String(telegramUrl || "").trim();
  if (!httpsUrl) return;

  const nativeUrl = telegramNativeMiniAppUrl(httpsUrl);
  if (!nativeUrl) {
    try { window.location.assign(httpsUrl); } catch { try { window.location.href = httpsUrl; } catch {} }
    return;
  }

  let fallbackTimer = 0;
  const clearFallback = () => {
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = 0;
    }
    document.removeEventListener("visibilitychange", clearOnLeave);
  };
  const clearOnLeave = () => {
    if (document.visibilityState === "hidden") clearFallback();
  };

  document.addEventListener("visibilitychange", clearOnLeave);
  fallbackTimer = window.setTimeout(() => {
    clearFallback();
    if (document.visibilityState !== "visible") return;
    try { window.location.assign(httpsUrl); } catch { try { window.location.href = httpsUrl; } catch {} }
  }, 1200);

  try {
    window.location.assign(nativeUrl);
  } catch {
    clearFallback();
    try { window.location.assign(httpsUrl); } catch { try { window.location.href = httpsUrl; } catch {} }
  }
}

function verificationErrorText(code, tr) {
  const normalized = String(code || "").toUpperCase();
  const map = {
    PHONE_INVALID: tr("registerWizard.errors.telegramPhone", "Укажите корректный Telegram-номер"),
    TOO_MANY_REQUESTS: tr("registerWizard.errors.phoneVerifyRate", "Слишком много попыток. Повторите немного позже."),
    PHONE_VERIFICATION_EXPIRED: tr("registerWizard.errors.phoneVerifyExpired", "Подтверждение истекло. Запустите его заново."),
    PHONE_VERIFICATION_MISMATCH: tr("registerWizard.errors.phoneVerifyMismatch", "Подтверждён другой номер. Повторите проверку."),
    TELEGRAM_PHONE_VERIFY_NOT_CONFIGURED: tr("registerWizard.errors.phoneVerifyUnavailable", "Telegram-подтверждение временно недоступно."),
  };
  return map[normalized] || tr("registerWizard.errors.phoneVerifyFailed", "Не удалось подтвердить номер через Telegram.");
}

export default function DriverTelegramPhoneVerification({
  phone,
  tr,
  beforeOpen,
  onStateChange,
}) {
  const [state, setState] = useState(() => readDriverPhoneVerificationState());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [transitionVisible, setTransitionVisible] = useState(false);

  const status = String(state?.status || "").toUpperCase();
  const verified = status === "VERIFIED";
  const sameInput = useMemo(() => {
    if (!state?.inputPhone) return true;
    return digits(state.inputPhone) === digits(phone);
  }, [phone, state?.inputPhone]);

  const publishState = useCallback((next) => {
    setState(next);
    saveDriverPhoneVerificationState(next);
    onStateChange?.(next);
  }, [onStateChange]);

  const clearState = useCallback(() => {
    publishState(null);
    setError("");
  }, [publishState]);

  const refreshStatus = useCallback(async (explicitToken = "") => {
    const token = String(explicitToken || state?.verificationToken || "").trim();
    if (!token) return null;

    try {
      const { response, data } = await reisorProMarketRequest(
        "/auth/phone-verification/status",
        { method: "POST", body: { verificationToken: token } }
      );

      if (!response.ok) {
        if ([404, 410].includes(Number(response.status))) clearState();
        return null;
      }

      const persisted = readDriverPhoneVerificationState();
      const next = {
        ...(persisted || {}),
        ...(state || {}),
        verificationToken: token,
        status: String(data?.status || "").toUpperCase(),
        phoneNormalized: String(data?.phoneNormalized || state?.phoneNormalized || persisted?.phoneNormalized || ""),
        expiresAt: data?.expiresAt || state?.expiresAt || persisted?.expiresAt || null,
        failureCode: String(data?.failureCode || ""),
        registrationCountry: REGISTRATION_COUNTRY,
      };
      publishState(next);

      if (next.status === "FAILED") {
        setError(
          next.failureCode === "PHONE_MISMATCH"
            ? tr("registerWizard.errors.phoneVerifyMismatch", "Telegram передал другой номер. Укажите свой номер и повторите проверку.")
            : verificationErrorText(next.failureCode, tr)
        );
      } else {
        setError("");
      }

      return next;
    } catch {
      return null;
    }
  }, [clearState, publishState, state, tr]);

  const start = useCallback(async () => {
    if (busy) return;
    const main = digits(phone);
    if (main.length < 7 || main.length > 15) {
      setError(tr("registerWizard.errors.telegramPhone", "Укажите корректный Telegram-номер"));
      return;
    }

    setBusy(true);
    setError("");
    setTransitionVisible(true);
    try {
      beforeOpen?.();
      const { response, data } = await reisorProMarketRequest(
        "/auth/phone-verification/start",
        {
          method: "POST",
          body: {
            role: ROLE,
            phone: String(phone || "").trim(),
            registrationCountry: REGISTRATION_COUNTRY,
          },
        }
      );

      if (!response.ok || !data?.verificationToken || !data?.telegramUrl) {
        setTransitionVisible(false);
        setError(verificationErrorText(data?.error, tr));
        return;
      }

      const next = {
        verificationToken: String(data.verificationToken),
        status: String(data.status || "PENDING").toUpperCase(),
        phoneNormalized: String(data.phoneNormalized || ""),
        expiresAt: data.expiresAt || null,
        failureCode: "",
        inputPhone: String(phone || "").trim(),
        registrationCountry: REGISTRATION_COUNTRY,
      };
      publishState(next);
      openTelegramUrl(data.telegramUrl);
    } catch {
      setTransitionVisible(false);
      setError(tr("registerWizard.errors.phoneVerifyUnavailable", "Telegram-подтверждение временно недоступно."));
    } finally {
      setBusy(false);
    }
  }, [beforeOpen, busy, phone, publishState, tr]);

  // Changing the phone invalidates an old proof in the UI. Server-side token remains
  // unusable for the new phone because consumeMarketPhoneVerification also matches phone.
  useEffect(() => {
    if (!state || sameInput) return;
    clearState();
  }, [clearState, sameInput, state]);

  // Restore after Android cold-start and refresh the durable Telegram result.
  useEffect(() => {
    const restored = readDriverPhoneVerificationState();
    if (!restored) return;

    const expiresAtMs = restored.expiresAt ? new Date(restored.expiresAt).getTime() : 0;
    if (expiresAtMs && expiresAtMs <= Date.now()) {
      saveDriverPhoneVerificationState(null);
      clearPhoneVerificationReturnSignal();
      setState(null);
      onStateChange?.(null);
      return;
    }

    setState(restored);
    onStateChange?.(restored);
    if (hasFreshPhoneVerificationReturnSignal()) clearPhoneVerificationReturnSignal();
    void refreshStatus(restored.verificationToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onReturn = () => {
      const restored = readDriverPhoneVerificationState();
      if (!restored?.verificationToken) return;
      setTransitionVisible(false);
      setState(restored);
      onStateChange?.(restored);
      clearPhoneVerificationReturnSignal();
      void refreshStatus(restored.verificationToken);
    };

    window.addEventListener("phone-verification-return", onReturn);
    return () => window.removeEventListener("phone-verification-return", onReturn);
  }, [onStateChange, refreshStatus]);

  useEffect(() => {
    let dead = false;
    let handle = null;

    const foreground = () => {
      if (dead || document.visibilityState === "hidden") return;
      setTransitionVisible(false);
      const restored = readDriverPhoneVerificationState();
      const token = String(restored?.verificationToken || state?.verificationToken || "").trim();
      const current = String(restored?.status || state?.status || "").toUpperCase();
      if (!token || ["VERIFIED", "CONSUMED", "FAILED", "EXPIRED", "SUPERSEDED"].includes(current)) return;
      void refreshStatus(token);
    };

    void CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) foreground();
    }).then((listener) => {
      if (dead) {
        try { listener?.remove?.(); } catch {}
        return;
      }
      handle = listener;
    }).catch(() => {});

    window.addEventListener("focus", foreground);
    document.addEventListener("visibilitychange", foreground);
    return () => {
      dead = true;
      try { handle?.remove?.(); } catch {}
      window.removeEventListener("focus", foreground);
      document.removeEventListener("visibilitychange", foreground);
    };
  }, [refreshStatus, state?.status, state?.verificationToken]);

  useEffect(() => {
    const token = String(state?.verificationToken || "").trim();
    if (!token || ["VERIFIED", "CONSUMED", "FAILED", "EXPIRED", "SUPERSEDED"].includes(status)) return undefined;
    const timer = window.setInterval(() => void refreshStatus(token), 1500);
    return () => window.clearInterval(timer);
  }, [refreshStatus, state?.verificationToken, status]);

  useEffect(() => {
    if (!transitionVisible) return undefined;
    const onResume = () => {
      if (document.visibilityState === "visible") setTransitionVisible(false);
    };
    const timer = window.setTimeout(() => setTransitionVisible(false), 10000);
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onResume);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [transitionVisible]);

  return (
    <div style={{ marginBottom: 14 }}>
      {verified ? (
        <div style={{ border: "1px solid rgba(52,211,153,.45)", background: "rgba(6,78,59,.34)", color: "#a7f3d0", borderRadius: 16, padding: "12px 14px", fontWeight: 900 }}>
          ✓ {tr("registerWizard.phoneVerified", "Номер подтверждён через Telegram")}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void start()}
          disabled={busy || transitionVisible}
          style={{ width: "100%", border: "1px solid #2563eb", background: "#2563eb", color: "white", borderRadius: 16, padding: 14, fontWeight: 900, opacity: busy || transitionVisible ? .65 : 1 }}
        >
          {busy || transitionVisible
            ? tr("registerWizard.phoneVerifyOpening", "Открываем Telegram…")
            : status === "PENDING" || status === "BOUND"
              ? tr("registerWizard.phoneVerifyContinue", "Продолжить подтверждение в Telegram")
              : tr("registerWizard.phoneVerifyButton", "Подтвердить номер через Telegram")}
        </button>
      )}

      {!verified && (status === "PENDING" || status === "BOUND") ? (
        <div style={{ color: "#93c5fd", fontSize: 12, lineHeight: 1.45, marginTop: 8 }}>
          {tr("registerWizard.phoneVerifyPending", "Передайте Telegram свой номер кнопкой контакта — REISOR проверит совпадение автоматически.")}
        </div>
      ) : null}

      {error ? (
        <div style={{ color: "#fecaca", fontSize: 12, lineHeight: 1.45, marginTop: 8 }}>{error}</div>
      ) : null}
    </div>
  );
}
